// =============================================================================
// app/javascript/application.js
// Shared bundle — loaded on every page.
// Bundled by esbuild → app/assets/builds/application.js (served by Propshaft)
// =============================================================================

// 1. jQuery global setup — MUST be first import so window.jQuery/window.$ is
//    set before any jQuery plugins (cocoon, select2, daterangepicker, etc.)
//    evaluate. See jquery-global.js for the full explanation.
import $ from "./jquery-global"

// 2. Turbo (replaces Turbolinks — fires turbo:load, turbo:before-cache)
import "@hotwired/turbo"

// 3. Rails UJS — data-confirm, data-disable-with, data-remote handlers
import Rails from "@rails/ujs"
Rails.start()

// 4. Active Storage — direct upload support
import * as ActiveStorage from "@rails/activestorage"
ActiveStorage.start()

// Popper (required by Bootstrap 5 for dropdowns, tooltips, modals)
import * as Popper from "@popperjs/core"
window.Popper = Popper

// Bootstrap 5 — imports Popper internally via npm, no window.Popper hack needed
import * as bootstrap from "bootstrap"
window.bootstrap = bootstrap

// 5. Cocoon — nested form add/remove (jQuery plugin)
import "@nathanvda/cocoon"

// 6. Moment + moment-range (must be global for daterangepicker and inline scripts)
import moment from "moment"
import { extendMoment } from "moment-range"
extendMoment(moment)
window.moment = moment

// 7. Select2 (jQuery plugin)
// select2's CJS export (dist/js/select2.js) is a factory: module.exports = function(root, jQuery).
// Side-effect import evaluates the module but does NOT call the factory, so $.fn.select2 is never
// registered. We must import and call it explicitly.
// NOTE: Do NOT import "select2/dist/js/i18n/cs" or "/en" — those files use select2's internal AMD
// loader (jQuery.fn.select2.amd.define) incompatible with esbuild. Language set in entry body below.
import select2Factory from "select2"
if (typeof select2Factory === "function") {
  select2Factory(window, $)
}

// 8. Daterangepicker (requires window.jQuery + window.moment)
import "daterangepicker"

// 9. js-cookie (assign to window for legacy inline scripts)
import Cookies from "js-cookie"
window.Cookies = Cookies

// 10. Typeahead (jQuery plugin) + Bloodhound suggestion engine
// corejs-typeahead's bundle.js has two UMD sections — the CJS module.exports is
// the typeahead jQuery plugin (overwrites Bloodhound). Import each separately:
//   "corejs-typeahead"              → registers $.fn.typeahead on window.$
//   "corejs-typeahead/dist/bloodhound" → exports the Bloodhound constructor
import "corejs-typeahead"
import Bloodhound from "corejs-typeahead/dist/bloodhound"
window.Bloodhound = Bloodhound  // expose for tagsInputTypeAhead() in plugins_config.js

// 11. Cookies EU consent banner (vendored — no npm package)
import "./vendor/cookies_eu"

// 11a. DataTables with Bootstrap 5 styling — registers $.fn.DataTable.
// Needed here (not just admin.js) because participant pages (e.g. the
// evaluations tables and registration_forms/list.html.erb) also call
// window.initDataTables(".data-tables") via common/plugins_config.js.
import "datatables.net-bs5"
import "datatables.net-fixedheader-bs5"

// 12. Application helpers (formerly app/assets/javascripts/common/)
import "./common/plugins_config"
import "./common/table"
import "./common/data-table-langs"
import "./common/evaluations"
import "./common/payment_assign"

// 13. Stimulus controllers
import "./controllers"

// =============================================================================
// Runtime event handlers (migrated from app/assets/javascripts/application.js)
// =============================================================================

// Configure Select2 Czech locale.
// select2/dist/js/i18n/cs.js uses select2's internal AMD loader and cannot be
// imported via esbuild — so we define the translations inline here instead.
// This runs in the entry body (after select2 import evaluates) ensuring
// $.fn.select2 is already registered when we call defaults.set().
if ($.fn.select2) {
  $.fn.select2.defaults.set("language", {
    errorLoading: () => "Výsledky nemohly být načteny.",
    inputTooLong: ({ input, maximum }) => {
      const n = input.length - maximum
      return n === 1 ? "Prosím, zadejte o jeden znak méně." : `Prosím, zadejte o ${n} znaků méně.`
    },
    inputTooShort: ({ minimum, input }) => {
      const n = minimum - input.length
      return n === 1 ? "Prosím, zadejte ještě jeden znak." : `Prosím, zadejte ještě dalších ${n} znaků.`
    },
    loadingMore: () => "Načítají se další výsledky…",
    maximumSelected: ({ maximum }) =>
      maximum === 1 ? "Můžete zvolit jen jednu položku." : `Můžete zvolit maximálně ${maximum} položek.`,
    noResults: () => "Nenalezeny žádné položky.",
    searching: () => "Vyhledávání…",
    removeAllItems: () => "Odstraňte všechny položky",
  })
}

$(document).on("turbo:before-cache", () => {
  // TODO: remove? aktualne sa nevola
  $("#messages div.alert").remove()

  // Reset Bootstrap modals synchronously so Turbo does not cache an open
  // modal (backdrop + body.modal-open) that becomes unclosable on Back.
  document.querySelectorAll(".modal.show").forEach((modal) => {
    bootstrap.Modal.getInstance(modal)?.dispose()
    modal.classList.remove("show")
    modal.style.display = "none"
    modal.setAttribute("aria-hidden", "true")
    modal.removeAttribute("aria-modal")
    modal.removeAttribute("role")
  })
  document.querySelectorAll(".modal-backdrop").forEach((el) => {
    el.remove()
  })
  document.body.classList.remove("modal-open")
  document.body.style.removeProperty("overflow")
  document.body.style.removeProperty("padding-right")
})

$(document).on("table:initialized", function () {
  initDatePickers(".filter-table.cloned .picker")
})

$(document).on("turbo:load", function () {
  "use strict"
  disableAutofill()
  initDatePickers(".picker")
  sidebar()
  anchorFix()
  tagsInputTypeAhead()
  initSelect2(".select2")

  $("div.nested-body:not(#user_rooms)")
    .on("cocoon:after-insert", function (e, added_item) {
      initDatePickers(added_item.find(".picker"))
      initSelect2(added_item.find(".select2"))
      if (typeof nestedTiny === "function") {
        nestedTiny()
      }
    })
    .on("cocoon:before-insert", function (e, insertedItem, originalEvent) {
      if ($(originalEvent.target).hasClass("disabled")) {
        e.preventDefault()
      }
    })
    .on("cocoon:after-remove", function (e, deletedItem) {
      deletedItem.find(":input").attr("required", false)
    })

  $("div#sections.nested-body").on("cocoon:after-insert", function (e, added_item) {
    added_item.find(".toggle-checks .btn:first-child").trigger("click")
  })
})

// Temporary bridge: forward turbo events to legacy turbolinks event names.
// Remove once all inline scripts have been migrated to turbo:load.
document.addEventListener("turbo:load", function () {
  $(document).trigger("turbolinks:load")
})

document.addEventListener("turbo:before-cache", function () {
  $(document).trigger("turbolinks:before-cache")
})
